import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { qaqcApi, materialsApi, usersApi, type PourCard } from '@/lib/apiClient';
import { StateView, Combobox } from '@/components';
import { POUR_TYPE_LABEL, pourStatus, pourStatusLabel, chipStyle, qtyDisplay, isRock, ROCK_BROWN, fmtDt, fmtWallD, fmtDTm, PourAction, MixFlag, useJobPicker } from './pourShared';
import PourCardDrawer from './PourCardDrawer';
import PourScheduleGrid from './PourScheduleGrid';
import PourLogTable from './PourLogTable';
import RockLogTable from './RockLogTable';
import AddPourCardForm from './AddPourCardForm';

// Company-wide QA/QC roll-up (top-level nav, like Receiving/Safety): the
// PEs' concrete-order work queue across every job — confirm, email, mark
// poured, cancel from one screen instead of visiting each project; add an
// order that never came through the form; and the Pour log (filter, sort,
// total and export every pour, for reconciling yards) with its sibling the
// Rock log (the same for Rock/Aggregate deliveries, totaled per unit). Row
// click / job link goes to the project's QA/QC tab for the full drawer
// (checklist, timeline, commitment link).

type Filter = 'action' | 'week' | 'open' | 'all';
type View = 'queue' | 'schedule' | 'log' | 'rock';
type SortKey = 'default' | 'job' | 'pour' | 'submitted';

function startOfDay(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

const css = `
  .qr-scroll { max-width: 1440px; margin: 0 auto; padding: var(--ew-s-5) var(--ew-s-6) var(--ew-s-8); }
  .qr-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .qr-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-100);
  }
  .qr-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .qr-table tr:last-child td { border-bottom: 0; }
  .qr-muted { color: var(--ew-n-500); }
  .qr-chip {
    display: inline-block; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-pill);
    padding: 2px 9px; font: var(--ew-fw-semibold) 11px/1.5 var(--ew-font); color: var(--ew-n-600); white-space: nowrap;
  }
  .qr-badge {
    display: inline-flex; align-items: center; gap: 6px; border-radius: var(--ew-r-pill);
    padding: 3px 10px; font: var(--ew-fw-semibold) 11px/1.4 var(--ew-font); white-space: nowrap;
  }
  .qr-pill {
    border: 1px solid var(--ew-n-200); background: #fff; color: var(--ew-n-600); cursor: pointer;
    border-radius: var(--ew-r-pill); padding: 6px 14px; font: var(--ew-fw-medium) 12px/1 var(--ew-font);
    transition: all var(--ew-dur-fast) var(--ew-ease);
  }
  .qr-pill:hover { border-color: var(--ew-n-400); }
  .qr-pill.active { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .qr-table tbody tr.qr-click { cursor: pointer; }
  .qr-table tbody tr.qr-click:hover td { background: var(--ew-n-50, #FAFAFA); }
`;

/** Inline "assign to job" picker for unmatched inspections (typo'd job # on
 *  the form). Pour cards get the same fix inside their drawer. */
function InspectionJobAssign({ inspectionId }: { inspectionId: number }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const jobPicker = useJobPicker();
  const assignMut = useMutation({
    mutationFn: (projectId: number) => qaqcApi.assignInspectionProject(inspectionId, projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['qaqc-inspections'] }),
  });
  return (
    <div style={{ minWidth: 220 }}>
      <div style={{ font: 'var(--ew-fw-semibold) 10px/1.5 var(--ew-font)', textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ew-safety-700)', marginBottom: 4 }}>
        No matching job — assign
      </div>
      <Combobox
        value={text}
        onChange={(v) => {
          setText(v);
          const pid = jobPicker.idByLabel.get(v);
          if (pid) assignMut.mutate(pid);
        }}
        options={jobPicker.options}
        placeholder="Type a job # or name…"
      />
    </div>
  );
}

export default function QaqcRollupPage() {
  const pourQuery = useQuery({ queryKey: ['qaqc-pours', 'all'], queryFn: () => qaqcApi.listPourCards() });
  const inspQuery = useQuery({ queryKey: ['qaqc-inspections', 'all'], queryFn: () => qaqcApi.listInspections() });

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('projects.edit') ?? false);

  const [filter, setFilter] = useState<Filter>('action');
  // Queue ↔ Schedule ↔ Pour log ↔ Rock log, URL-backed so PEs can bookmark /qaqc?view=schedule.
  const [params, setParams] = useSearchParams();
  const rawView = params.get('view');
  const view: View = rawView === 'schedule' || rawView === 'log' || rawView === 'rock' ? rawView : 'queue';
  const setView = (v: View) =>
    setParams(v === 'queue' ? {} : { view: v }, { replace: true });
  // Office-entered order (phoned in, or the form app was down).
  const [addingOrder, setAddingOrder] = useState(false);
  // Search matches job #, name, element, mix, and vendor — both views.
  const [search, setSearch] = useState('');
  // Queue sort: default = status rank then pour date (server order).
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'default', dir: 1 });
  const toggleSort = (key: Exclude<SortKey, 'default'>) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  // Full pour-card drawer (same as the project tab — the PEs manage from
  // here, so the pre-pour checklist detail must be one click away). The
  // commitment picker needs the card's project's materials — fetched lazily
  // when a card opens.
  const [openCard, setOpenCard] = useState<number | null>(null);
  const openedCard = useMemo(
    () => (pourQuery.data ?? []).find((c) => c.id === openCard) ?? null,
    [pourQuery.data, openCard],
  );
  const { data: drawerMaterials } = useQuery({
    queryKey: ['materials', openedCard?.projectId],
    queryFn: () => materialsApi.list({ projectId: openedCard!.projectId! }),
    enabled: openedCard?.projectId != null,
    staleTime: 60_000,
  });

  const cards = pourQuery.data ?? [];
  const now = startOfDay(new Date());
  const weekEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const isOpen = (c: PourCard) => c.status === 'pending' || c.status === 'confirmed';
  const inWeek = (c: PourCard) => {
    if (!c.pourDatetime) return false;
    const d = new Date(c.pourDatetime);
    return d >= now && d < weekEnd;
  };

  const kpis = useMemo(() => ({
    pending: cards.filter((c) => c.status === 'pending').length,
    // Concrete and rock counted apart: a delivery of stone isn't a pour.
    week: cards.filter((c) => !isRock(c) && isOpen(c) && inWeek(c)).length,
    rockWeek: cards.filter((c) => isRock(c) && isOpen(c) && inWeek(c)).length,
    cyWeek: cards
      .filter((c) => !isRock(c) && c.status === 'poured' && c.pouredAt && new Date(c.pouredAt) >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000))
      .reduce((s, c) => s + (c.actualQtyCy ?? c.qtyCy ?? 0), 0),
  }), [cards, now]);

  // Search first (shared by both views), then the queue's status filter + sort.
  const q = search.trim().toLowerCase();
  const searched = useMemo(() => (q === '' ? cards : cards.filter((c) =>
    [c.project?.jobNumber, c.jobNumberRaw, c.project?.title, c.element, c.mix, c.vendor]
      .some((v) => v != null && v.toLowerCase().includes(q)))), [cards, q]);

  const visible = useMemo(() => {
    const filtered = searched.filter((c) =>
      filter === 'action' ? c.status === 'pending'
      : filter === 'week' ? isOpen(c) && inWeek(c)
      : filter === 'open' ? isOpen(c)
      : true);
    if (sort.key === 'default') return filtered;
    const val = (c: PourCard) =>
      sort.key === 'job' ? (c.project?.jobNumber ?? c.jobNumberRaw ?? '')
      : sort.key === 'pour' ? (c.pourDatetime ?? '')
      : c.createdAt;
    return [...filtered].sort((a, b) => sort.dir * String(val(a)).localeCompare(String(val(b)), undefined, { numeric: true }));
  }, [searched, filter, sort]);

  // Supersede hint per job: an open will-call older than the job's newest
  // Main Pour card was probably firmed up via a new form — the office
  // cancels it (same logic as the project tab, computed per project here).
  const newestMainByProject = useMemo(() => {
    const m = new Map<number, number>();
    for (const c of cards) {
      if (c.pourType !== 'main' || c.projectId == null) continue;
      const t = new Date(c.createdAt).getTime();
      if (t > (m.get(c.projectId) ?? 0)) m.set(c.projectId, t);
    }
    return m;
  }, [cards]);
  const isSuperseded = (c: PourCard) =>
    c.pourType === 'willcall' && isOpen(c) && c.projectId != null
    && new Date(c.createdAt).getTime() < (newestMainByProject.get(c.projectId) ?? 0);

  const flaggedInspections = (inspQuery.data ?? []).filter((i) => i.noCount > 0);

  const kpiTiles = [
    { label: 'Awaiting Office Confirm', value: String(kpis.pending), accent: 'var(--ew-performance)' },
    { label: 'Pours Next 7 Days',       value: String(kpis.week),    accent: 'var(--ew-quality)' },
    // Open rock orders due in the next 7 days; brown = the schedule's rock color.
    { label: 'Rock Deliveries · Next 7 Days', value: String(kpis.rockWeek), accent: ROCK_BROWN },
    { label: 'CY Placed · Last 7 Days', value: String(Math.round(kpis.cyWeek)), accent: 'var(--ew-green)' },
    { label: 'Flagged Inspections',     value: String(flaggedInspections.length), accent: 'var(--ew-safety)' },
  ];

  return (
    <div className="qr-scroll">
      <style>{css}</style>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>QA / QC</h2>
        <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>◇ Synced from Formstack</span>
        <span style={{ font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>
          Concrete &amp; rock orders and inspections across every job — open a job for the full pour card
        </span>
      </div>

      {/* ── KPI tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-4)' }}>
        {kpiTiles.map((t) => (
          <div key={t.label} style={{ background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: 'var(--ew-s-4)', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-1)' }}>
            <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: t.accent }} />
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>{t.label}</span>
            <span style={{ font: 'var(--ew-fw-semibold) 22px/1.1 var(--ew-font)', fontVariantNumeric: 'tabular-nums' }}>{t.value}</span>
          </div>
        ))}
      </div>

      {/* ── Pour Cards queue ── */}
      <div style={{ background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', marginBottom: 'var(--ew-s-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', padding: '14px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)', flexWrap: 'wrap' }}>
          <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)', margin: 0 }}>
            Pour Cards · Concrete &amp; Rock Orders
          </h3>
          {/* Queue ↔ Schedule (the PEs' Excel "Concrete Pour Schedule", fed live by the cards) */}
          <div style={{ display: 'flex', gap: 0, border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-pill)', overflow: 'hidden' }}>
            {(['queue', 'schedule', 'log', 'rock'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)} style={{
                border: 0, cursor: 'pointer', padding: '6px 14px',
                font: 'var(--ew-fw-medium) 12px/1 var(--ew-font)',
                background: view === v ? 'var(--ew-dark)' : '#fff',
                color: view === v ? '#fff' : 'var(--ew-n-600)',
              }}>{v === 'queue' ? 'Work queue' : v === 'schedule' ? 'Schedule' : v === 'log' ? 'Pour log' : 'Rock log'}</button>
            ))}
          </div>
          {/* The Pour log has its own search alongside its other filters. */}
          {view !== 'log' && view !== 'rock' && <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job, element, mix, vendor…"
            style={{
              border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px',
              font: 'var(--ew-fw-regular) 12px var(--ew-font)', width: 230, background: '#fff', color: 'var(--ew-dark)',
            }}
          />}
          {canEdit && !addingOrder && (
            <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }}
                    title="Add an order that didn't come through Formstack (Pre-Pour Checklist or Rock/Aggregate form)"
                    onClick={() => setAddingOrder(true)}>
              + Add order
            </button>
          )}
          {view === 'queue' && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className={`qr-pill${filter === 'action' ? ' active' : ''}`} onClick={() => setFilter('action')}>Needs confirmation</button>
              <button className={`qr-pill${filter === 'week' ? ' active' : ''}`} onClick={() => setFilter('week')}>Due this week</button>
              <button className={`qr-pill${filter === 'open' ? ' active' : ''}`} onClick={() => setFilter('open')}>All open</button>
              <button className={`qr-pill${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>Everything</button>
            </div>
          )}
        </div>
        {addingOrder && (
          <AddPourCardForm
            onDone={() => setAddingOrder(false)}
            onSaved={(card) => {
              // A confirmed entry would sit behind "Needs confirmation" and look
              // like it never saved, so widen the queue and open the new card.
              if (view === 'queue' && filter === 'action' && card.status !== 'pending') setFilter('open');
              setOpenCard(card.id);
            }}
          />
        )}
        {view === 'schedule' ? (
          <StateView query={pourQuery} empty="No pour cards yet — they arrive when the field submits Pre-Pour Checklists or Rock/Aggregate orders.">
            {() => <PourScheduleGrid cards={searched} onOpen={setOpenCard} />}
          </StateView>
        ) : view === 'rock' ? (
          <StateView query={pourQuery} empty="No rock deliveries yet — they arrive when the field submits the Rock/Aggregate form.">
            {(all) => <RockLogTable cards={all} showJob fileName="rock-log-all-jobs" onOpen={setOpenCard} />}
          </StateView>
        ) : view === 'log' ? (
          <StateView query={pourQuery} empty="No pour cards yet — they arrive when the field submits Pre-Pour Checklists or Rock/Aggregate orders.">
            {(all) => <PourLogTable cards={all} showJob fileName="pour-log-all-jobs" onOpen={setOpenCard} />}
          </StateView>
        ) : (
        <StateView query={pourQuery} empty="No pour cards yet — they arrive when the field submits Pre-Pour Checklists or Rock/Aggregate orders.">
          {() => visible.length === 0 ? (
            <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
              {filter === 'action' ? 'Nothing awaiting confirmation — the queue is clear.' : 'Nothing matches this filter.'}
            </div>
          ) : (
            <div className="ew-table-scroll">
              <table className="qr-table ew-cards">
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('job')}>Job{sort.key === 'job' ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}</th>
                    <th>Element</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('pour')}>Pour Date{sort.key === 'pour' ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}</th>
                    <th>Mix ID</th>
                    <th>Qty</th>
                    <th>Pour Type</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('submitted')} title="When the order came in — the field's Pre-Pour Checklist, or the office entry">Submitted{sort.key === 'submitted' ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}</th>
                    <th>Order</th>
                    {canEdit ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c) => {
                    const st = pourStatus(c.status);
                    const cs = chipStyle(c.status, c.pourType);
                    return (
                      <tr key={c.id} className="qr-click" onClick={() => setOpenCard(c.id)}>
                        <td data-label="Job" onClick={c.project ? (e) => e.stopPropagation() : undefined}>
                          {c.project ? (
                            <Link to={`/projects/${c.project.id}?tab=qaqc`} style={{ color: 'var(--ew-green-700)', fontWeight: 600, textDecoration: 'none' }}>
                              {c.project.jobNumber}
                            </Link>
                          ) : (
                            <span title="The job number on the form didn't match a project — open the card to assign the right job">
                              <span className="qr-muted">{c.jobNumberRaw ?? '—'}</span>
                              <span style={{ display: 'block', font: 'var(--ew-fw-semibold) 10px/1.5 var(--ew-font)', textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ew-safety-700)', marginTop: 2 }}>
                                No matching job — assign
                              </span>
                            </span>
                          )}
                          {c.project && <div className="qr-muted" style={{ fontSize: 12, marginTop: 2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.project.title}</div>}
                        </td>
                        <td data-label="Element">
                          <div style={{ fontWeight: 500 }}>
                            {c.element ?? '—'}
                            {c.source === 'manual' && (
                              <span className="qr-chip" style={{ marginLeft: 8 }}
                                    title={`Entered by the office${c.createdBy ? ` (${c.createdBy})` : ''} — no ${isRock(c) ? 'Rock/Aggregate form' : 'Pre-Pour Checklist'} behind it`}>
                                Office entry
                              </span>
                            )}
                            {isSuperseded(c) && (
                              <span style={{ marginLeft: 8, font: 'var(--ew-fw-semibold) 10px/1.5 var(--ew-font)', textTransform: 'uppercase', letterSpacing: '.04em', background: 'var(--ew-safety-50)', color: 'var(--ew-safety-700)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-pill)', padding: '2px 8px' }}
                                title="A newer Main Pour card arrived after this will-call — it's likely been firmed up. Use 'Confirmed with main pour' if the concrete came in with the main pour (or Cancel if it's called off).">
                                New main pour received
                              </span>
                            )}
                          </div>
                          {c.location && <div className="qr-muted" style={{ fontSize: 12, marginTop: 2 }}>{c.location}</div>}
                        </td>
                        <td data-label="Pour Date" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDt(c.pourDatetime)}</td>
                        <td className="qr-muted" data-label="Mix ID">{c.mix ?? '—'}<MixFlag card={c} size="sm" /></td>
                        <td className="qr-muted" data-label="Qty">{qtyDisplay(c) ?? '—'}</td>
                        <td data-label="Pour Type"><span className="qr-chip" style={{ background: cs.bg, border: cs.border, color: cs.fg ?? 'var(--ew-dark)', textDecoration: cs.strike ? 'line-through' : undefined }}>{POUR_TYPE_LABEL[c.pourType] ?? c.pourType}</span></td>
                        <td className="qr-muted" data-label="Submitted" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDTm(c.createdAt)}</td>
                        <td data-label="Order">
                          <span className="qr-badge" style={{ background: st.bg, color: st.color }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: st.dot }} />
                            {pourStatusLabel(c.status, c.pourType)}
                          </span>
                        </td>
                        {canEdit ? (
                          <td onClick={(e) => e.stopPropagation()}>
                            <PourAction card={c} jobNumber={c.project?.jobNumber ?? c.jobNumberRaw ?? ''} size="sm" />
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </StateView>
        )}
      </div>

      {/* ── Flagged inspections across jobs ── */}
      <div style={{ background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)' }}>
        <div style={{ padding: '14px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)' }}>
          <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)', margin: 0 }}>
            Flagged Inspections
          </h3>
        </div>
        <StateView query={inspQuery} empty="No inspections yet.">
          {() => flaggedInspections.length === 0 ? (
            <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
              No flagged inspections — every submitted report passed. Full inspection history lives on each job's QA/QC tab.
            </div>
          ) : (
            <div className="ew-table-scroll">
              <table className="qr-table ew-cards">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Inspector</th>
                    <th>Date</th>
                    <th>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {flaggedInspections.map((i) => (
                    <tr key={i.id}>
                      <td data-label="Job">
                        {i.project ? (
                          <Link to={`/projects/${i.project.id}?tab=qaqc`} style={{ color: 'var(--ew-green-700)', fontWeight: 600, textDecoration: 'none' }}>
                            {i.project.jobNumber}
                          </Link>
                        ) : canEdit ? (
                          <InspectionJobAssign inspectionId={i.id} />
                        ) : <span className="qr-muted">—</span>}
                        {i.jobText && <div className="qr-muted" style={{ fontSize: 12, marginTop: 2 }}>{i.jobText}</div>}
                      </td>
                      <td className="qr-muted" data-label="Inspector">{i.inspectorName ?? '—'}{i.inspectorTitle ? ` · ${i.inspectorTitle}` : ''}</td>
                      <td data-label="Date" style={{ whiteSpace: 'nowrap' }}>{fmtWallD(i.inspectDate)}</td>
                      <td data-label="Flags">
                        <span className="qr-badge" style={{ background: 'var(--ew-danger-50)', color: 'var(--ew-danger-700)' }}>
                          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--ew-danger)' }} />
                          {i.noCount} item{i.noCount === 1 ? '' : 's'} flagged
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </StateView>
      </div>

      {openedCard && (
        <PourCardDrawer
          card={openedCard}
          jobNumber={openedCard.project?.jobNumber ?? openedCard.jobNumberRaw ?? ''}
          canEdit={canEdit}
          materials={drawerMaterials ?? []}
          onClose={() => setOpenCard(null)}
        />
      )}
    </div>
  );
}
